import type { TrainingEvalEnvProgressView, TrainingEvalRunProgressView } from "@/api/trainingEval";

const EPISODE_RE =
  /\benv\s+(\d+)(?::|\s+via\b)[^\n]*?\bep\s+(\d+)\s*\/\s*(\d+)(?:[^\n]*?\(true=(\d+)\s+fp=(\d+)(?:\s+fail=(\d+)\s+timeout=(\d+))?\))?/g;
const TARGETS_RE =
  /\[episode-targets\][^\n]*?\btotal=(\d+)[^\n]*?\bnum_envs=(\d+)(?:[^\n]*?\bper_env=\[([\d,\s]*)\])?/;
const NUM_ENVS_RE = /overriding cfg\.num_envs:\s*\d+\s*->\s*(\d+)/;
const WARMUP_RE = /\bWarmup\s+(\d+)\/(\d+)/g;
const MILESTONES: ReadonlyArray<{ pattern: RegExp; label: string }> = [
  { pattern: /\[entrypoint\][^\n]*patched eval_run/, label: "Policy 내려받는 중…" },
  { pattern: /\[entrypoint\][^\n]*policy downloaded/, label: "Simulator 초기화 중…" },
  { pattern: /\[INFO\]: Starting the simulation/, label: "Simulation 시작 중…" },
  {
    pattern: /\[INFO\]: Time taken for simulation start/,
    label: "환경 구성 중…",
  },
  {
    pattern: /\[INFO\]: Completed setting up the environment/,
    label: "Inference Model 불러오는 중…",
  },
];

type EvalStage = "booting" | "warmup" | "episodes";

type EnvOutcomes = {
  success: number;
  fp: number;
  fail: number;
  timeout: number;
};

const ZERO_OUTCOMES: EnvOutcomes = {
  success: 0,
  fp: 0,
  fail: 0,
  timeout: 0,
};

export type EvalLogProgress = {
  completed: number;
  total: number | null;
  stage: EvalStage;
  stageDetail: string | null;
  warmup: { done: number; total: number } | null;
  numEnvs: number | null;
  perEnvTargets: number[] | null;
  episodesPerEnv: number | null;
  envEpisodes: number[];
  envOutcomes: EnvOutcomes[];
};

export type EvalLogProgressTracker = {
  feed: (text: string) => boolean;
  progress: () => EvalLogProgress;
};

export function createEvalLogProgressTracker(): EvalLogProgressTracker {
  const maxEpisodeByEnv = new Map<number, number>();
  const outcomesByEnv = new Map<number, EnvOutcomes>();
  let completed = 0;
  let total: number | null = null;
  let milestone = -1;
  let warmup: { done: number; total: number } | null = null;
  let numEnvs: number | null = null;
  let perEnvTargets: number[] | null = null;
  let episodesPerEnv: number | null = null;
  let sawEpisodes = false;

  function stage(): EvalStage {
    if (sawEpisodes) return "episodes";
    return warmup === null ? "booting" : "warmup";
  }

  function envCount(): number {
    if (numEnvs !== null) return numEnvs;
    return maxEpisodeByEnv.size === 0 ? 0 : Math.max(...maxEpisodeByEnv.keys()) + 1;
  }

  return {
    feed(text: string): boolean {
      let advanced = false;

      for (let index = MILESTONES.length - 1; index > milestone; index -= 1) {
        if (MILESTONES[index].pattern.test(text)) {
          milestone = index;
          advanced = true;
          break;
        }
      }

      if (numEnvs === null) {
        const match = NUM_ENVS_RE.exec(text);
        if (match !== null) {
          numEnvs = Number(match[1]);
          advanced = true;
        }
      }

      WARMUP_RE.lastIndex = 0;
      for (let match = WARMUP_RE.exec(text); match !== null; match = WARMUP_RE.exec(text)) {
        const done = Number(match[1]);
        if (done > (warmup?.done ?? 0)) {
          warmup = { done, total: Number(match[2]) };
          advanced = true;
        }
      }
      WARMUP_RE.lastIndex = 0;

      const targets = TARGETS_RE.exec(text);
      if (targets !== null) {
        if (!sawEpisodes || total === null) advanced = true;
        sawEpisodes = true;
        total ??= Number(targets[1]);
        numEnvs = Number(targets[2]);
        if (perEnvTargets === null && targets[3]) {
          perEnvTargets = targets[3].split(",").map((value) => Number(value.trim()));
        }
      }

      EPISODE_RE.lastIndex = 0;
      for (let match = EPISODE_RE.exec(text); match !== null; match = EPISODE_RE.exec(text)) {
        sawEpisodes = true;
        episodesPerEnv ??= Number(match[3]);
        const envIndex = Number(match[1]);
        const episode = Number(match[2]);
        const previous = maxEpisodeByEnv.get(envIndex) ?? 0;
        if (episode <= previous) continue;
        maxEpisodeByEnv.set(envIndex, episode);
        completed += episode - previous;
        if (match[4] !== undefined) {
          outcomesByEnv.set(envIndex, {
            success: Number(match[4]),
            fp: Number(match[5]),
            fail: Number(match[6] ?? 0),
            timeout: Number(match[7] ?? 0),
          });
        }
        advanced = true;
      }
      EPISODE_RE.lastIndex = 0;

      return advanced;
    },
    progress(): EvalLogProgress {
      return {
        completed,
        total,
        stage: stage(),
        stageDetail: milestone < 0 ? null : MILESTONES[milestone].label,
        warmup: warmup === null ? null : { ...warmup },
        numEnvs,
        perEnvTargets: perEnvTargets === null ? null : [...perEnvTargets],
        episodesPerEnv,
        envEpisodes: Array.from(
          { length: envCount() },
          (_, envIndex) => maxEpisodeByEnv.get(envIndex) ?? 0,
        ),
        envOutcomes: Array.from(
          { length: envCount() },
          (_, envIndex) => outcomesByEnv.get(envIndex) ?? ZERO_OUTCOMES,
        ),
      };
    },
  };
}

export function parseEvalLogProgress(log: string): EvalLogProgress {
  const tracker = createEvalLogProgressTracker();
  tracker.feed(log);
  return tracker.progress();
}

export function mergeEvalLogProgress(
  backend: TrainingEvalRunProgressView | null,
  log: EvalLogProgress,
  evalRunId: string,
  status: string,
): TrainingEvalRunProgressView | null {
  if (
    backend === null &&
    log.completed === 0 &&
    log.envEpisodes.length === 0 &&
    log.stage === "booting"
  ) {
    return null;
  }

  const completed =
    backend?.completed ?? (log.completed > 0 || log.stage === "episodes" ? log.completed : null);
  const total = backend?.total ?? log.total;
  const percent =
    backend?.percent ??
    (completed !== null && total ? Math.min(100, (completed / total) * 100) : null);
  const numEnvs = backend?.num_envs ?? log.numEnvs;
  const perEnvTargets = backend?.per_env_targets ?? log.perEnvTargets;
  const episodesPerEnv = backend?.episodes_per_env ?? log.episodesPerEnv;
  let envs = backend?.envs ?? null;
  if ((envs === null || envs.length === 0) && log.envEpisodes.length > 0) {
    envs = log.envEpisodes.map(
      (done, index): TrainingEvalEnvProgressView => ({
        index,
        done,
        target: perEnvTargets?.[index] ?? episodesPerEnv ?? 0,
        success: log.envOutcomes[index]?.success ?? 0,
        fp: log.envOutcomes[index]?.fp ?? 0,
        fail: log.envOutcomes[index]?.fail ?? 0,
        timeout: log.envOutcomes[index]?.timeout ?? 0,
      }),
    );
  }

  return {
    eval_run_id: backend?.eval_run_id ?? evalRunId,
    status: backend?.status ?? status,
    phase: backend?.phase ?? log.stageDetail ?? log.stage,
    completed,
    total,
    remaining: backend?.remaining ?? null,
    percent,
    eta_seconds: backend?.eta_seconds ?? null,
    source: backend?.source ?? "logs",
    updated_at: backend?.updated_at ?? null,
    heartbeat_age_seconds: backend?.heartbeat_age_seconds ?? null,
    stale: backend?.stale ?? false,
    observed_error: backend?.observed_error ?? null,
    num_envs: numEnvs,
    per_env_targets: perEnvTargets,
    episodes_per_env: episodesPerEnv,
    envs,
  };
}
